import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardHeader from './DashboardHeader';
import DashboardBottomNav from './DashboardBottomNav';
import DesktopNav from './DesktopNav';
import { useNotificationStore } from '../stores/notificationStore';
import { useUserStore } from '../stores/userStore';
import { requestNotificationPermission, setupMessageListener } from '../services/pushNotificationService';
import toast from 'react-hot-toast';

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
}

const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const fetchUnreadCount = useNotificationStore((state) => state.fetchUnreadCount);
  const { user } = useUserStore();
  const [pushBannerDismissed, setPushBannerDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem('push_banner_dismissed') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    // Initial fetch
    fetchUnreadCount();

    // Poll every 60 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 60000);

    // Check current permission before requesting
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        requestNotificationPermission().then((token) => {
          if (token) {
            // Token obtained via existing permission
          }
        });
      } else if (Notification.permission === 'default') {
        // Awaiting user gesture to request permission
      } else {
        // Notification permission denied
      }
    }

    // Setup foreground message listener
    const unsubscribe = setupMessageListener((payload) => {
      toast(
        (_t) => (
          <div className="flex flex-col gap-1 cursor-pointer" onClick={() => toast.dismiss(_t.id)}>
            <span className="font-semibold text-gray-900">{payload.notification?.title}</span>
            <span className="text-sm text-gray-600">{payload.notification?.body}</span>
          </div>
        ),
        { duration: 5000, position: 'top-center' }
      );
    });

    return () => {
      clearInterval(interval);
      if (unsubscribe) unsubscribe();
    };
  }, [fetchUnreadCount]);

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />
      <DesktopNav />
      <div className="pb-24 md:pb-6">
        {!user?.is_employer &&
          ('Notification' in window) &&
          Notification.permission !== 'granted' &&
          !pushBannerDismissed && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
            <div className="bg-indigo-600 text-white px-4 py-3 rounded-lg flex justify-between items-center shadow-sm">
              <div className="flex flex-col">
                <span className="font-semibold text-sm">{t('push_banner.title')}</span>
                <span className="text-xs text-indigo-100">{t('push_banner.body')}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => {
                    requestNotificationPermission().then((token) => {
                      if (token) {
                        toast.success(t('push_banner.enabled'));
                        try { localStorage.setItem('push_banner_dismissed', 'true'); } catch { /* ignore */ }
                        setPushBannerDismissed(true);
                      }
                    });
                  }}
                  className="bg-white text-indigo-600 text-sm font-semibold py-1.5 px-3 rounded-md hover:bg-indigo-50 transition-colors"
                >
                  {t('push_banner.enable')}
                </button>
                <button
                  onClick={() => {
                    try { localStorage.setItem('push_banner_dismissed', 'true'); } catch { /* ignore */ }
                    setPushBannerDismissed(true);
                  }}
                  className="text-indigo-200 hover:text-white text-lg leading-none px-1"
                  aria-label={t('push_banner.dismiss')}
                >
                  &times;
                </button>
              </div>
            </div>
          </div>
        )}

        {children}
      </div>
      <DashboardBottomNav />
    </div>
  );
};

export default AuthenticatedLayout;
